import { debounce, find, first, map, toNumber } from "lodash";
import React, { useCallback, useRef } from "react";
import { AssessmentEvaluation } from "@/features/people/graphql/generated";
import { ScorecardRow } from "@/features/scorecards/graphql/generated";
import { Note } from "@/features/workspace/graphql/generated";
import { Column, Row } from "@/shared/ui/Blocks";
import { IconName } from "@/shared/ui/Icon";
import { Title } from "@/shared/ui/Title";
import IconBadge from "@/shared/ui/IconBadge";
import { AssessmentRow } from "@/features/assessments/components/AssessmentRow";
import { FocusWrapper } from "@/shared/ui/FocusWrapper";
import { cn } from "@/shared/utils/shadcn";

export interface ScorecardRowWithEvaluation extends ScorecardRow {
  evaluations: AssessmentEvaluation[];
  note?: Note;
}

interface AssessmentRowGroupsListProps {
  groups: Record<string, ScorecardRowWithEvaluation[]>;
  onEvaluate: (
    rowId: number,
    evaluation: number,
    meta?: Record<string, any>
  ) => void;
  onSummaryEdit: (note: Note, text: string) => void;
  onRowFocus?: (row?: ScorecardRowWithEvaluation) => void;
  focusedRow?: ScorecardRowWithEvaluation;
  variant?: "report" | "assessment";
  tagsData?: any;
}
export function AssessmentRowGroupsList(props: AssessmentRowGroupsListProps) {
  const {
    groups,
    onEvaluate,
    onSummaryEdit,
    onRowFocus,
    focusedRow,
    variant,
    tagsData,
  } = props;

  const debouncedFunctionsRef = useRef<Record<number, any>>({});

  const onExpand = useCallback(
    (row: ScorecardRow) => () => {
      onRowFocus?.(
        focusedRow?.id === row.id
          ? undefined
          : (row as ScorecardRowWithEvaluation)
      );
    },
    [onRowFocus, focusedRow]
  );

  const handleEvaluate = useCallback(
    (rowId: number) => (score: number, meta?: Record<string, any>) => {
      onEvaluate(rowId, score, meta);
    },
    [onEvaluate]
  );

  const handleSpanScoreChange = useCallback(
    (rowId: number) => (mainScore: number, spanScores: [number, number]) => {
      onEvaluate(rowId, mainScore, {
        type: "leadership_span",
        score_left: spanScores[0],
        score_right: spanScores[1],
      });
    },
    [onEvaluate]
  );

  const handleNotesChange = useCallback(
    (row: ScorecardRowWithEvaluation) => (notes?: string) => {
      if (row.id && !debouncedFunctionsRef.current[row.id]) {
        debouncedFunctionsRef.current[row.id] = debounce((notes: string) => {
          const note = {
            ...(row.note || {}),
            row_id: row?.id,
            notes,
          } as Note;

          onSummaryEdit(note, notes);
        }, 3000);
      }

      // Call the debounced function

      if (row?.id) {
        debouncedFunctionsRef.current[row?.id](notes);
      }
    },
    [onSummaryEdit]
  );

  return (
    <Column className="space-y-24">
      {map(groups, (rows, title) => (
        <Column key={title}>
          <Row
            className={cn("gap-4", variant === "report" && "p-4 bg-blue-100")}
          >
            {variant !== "report" && (
              <Column>
                <IconBadge
                  icon={(first(rows)?.icon as IconName) || "Flag"}
                  size="xs"
                  color="blue"
                />
              </Column>
            )}
            <Title as={variant === "assessment" ? "h6" : "h5"}>{title}</Title>
          </Row>
          <Column className="gap-4 mt-8">
            {map(rows, (row) => {
              const rowEvaluation = find(
                row.evaluations,
                (evaluation) => evaluation?.row_id === row?.id
              );

              return (
                <FocusWrapper
                  key={row!.id}
                  isFocused={row?.id === focusedRow?.id}
                >
                  <AssessmentRow
                    {...row}
                    summary={row.note?.text || ""}
                    key={row!.id}
                    score={toNumber(rowEvaluation?.evaluation)}
                    spanScore={
                      row?.meta?.type === "leadership_span"
                        ? [
                            rowEvaluation?.meta?.score_left,
                            rowEvaluation?.meta?.score_right,
                          ]
                        : undefined
                    }
                    leadershipSpan={
                      row?.meta?.type === "leadership_span"
                        ? (row?.meta as any)
                        : undefined
                    }
                    descriptionLimit={500}
                    variant={variant === "report" ? "default" : "wisgpt"}
                    onScoreChange={handleEvaluate(toNumber(row!.id))}
                    onSpanScoreChange={handleSpanScoreChange(toNumber(row!.id))}
                    onNotesChange={handleNotesChange(row)}
                    isExpanded={!!row?.id && row?.id === focusedRow?.id}
                    onExpand={onExpand(row!)}
                    tagsData={tagsData}
                  />
                </FocusWrapper>
              );
            })}
          </Column>
        </Column>
      ))}
    </Column>
  );
}

export default AssessmentRowGroupsList;
